import React from "react";
import { Alert, Grid, TextField } from "@mui/material";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ISessionState } from "../../../../models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import * as documentService from "../../../../models/services/documentService";
import { AppUtils } from "source/framework/appUtils";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { SaveDataObj } from "source/framework/saveData";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { DocumentSettingsSave } from "source/models/documentSettings/documentSettingsSave";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";

interface IDocumentOtherSettingsProps {
    sessionState: ISessionState;
    companyId: string;
}

interface IDocumentOtherSettingsState {
    initialized: boolean;
    ignoreFolderPatterns: string;
    ignoreFilePatterns: string;
    documentVersionAutoSaveHistorySize: string;
    documentVersionPublishHistorySize: string;
}

export interface IDocumentOtherSettingsCancelResult {
    canChangePage: boolean;
    refreshFolders: boolean;
}

export class DocumentOtherSettings extends React.Component<IDocumentOtherSettingsProps, IDocumentOtherSettingsState> {
    private orgStateHash: string = "";

    constructor(props: IDocumentOtherSettingsProps) {
        super(props);
        this.state = {
            initialized: false,
            ignoreFolderPatterns: "",
            ignoreFilePatterns: "",
            documentVersionAutoSaveHistorySize: "0",
            documentVersionPublishHistorySize: "1",
        };
    }

    loadData = async () => {
        const result = await AppUtils.callService(() => documentService.getDocumentSettings(this.props.companyId), false);
        if (!result) return;
        const newState = {
            initialized: true,
            ignoreFolderPatterns: result.ignoreFolderPatterns,
            ignoreFilePatterns: result.ignoreFilePatterns,
            documentVersionAutoSaveHistorySize: result.documentVersionAutoSaveHistorySize.toString(10),
            documentVersionPublishHistorySize: result.documentVersionPublishHistorySize.toString(10)
        };
        this.setState(newState);
        this.orgStateHash = this.getSaveDataFromState(newState).hash;
    };

    componentDidMount(): void {
        this.loadData();
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "ignoreFolderPatterns") {
            this.setState({ ignoreFolderPatterns: value });
        } else if (name === "ignoreFilePatterns") {
            this.setState({ ignoreFilePatterns: value });
        }
    };

    getSaveDataFromState = (aState: IDocumentOtherSettingsState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", this.props.companyId);
        result.append("ignoreFolderPatterns", aState.ignoreFolderPatterns);
        result.append("ignoreFilePatterns", aState.ignoreFilePatterns);
        result.append("documentVersionAutoSaveHistorySize", aState.documentVersionAutoSaveHistorySize.toInteger());
        result.append("documentVersionPublishHistorySize", aState.documentVersionPublishHistorySize.toInteger());
        return result;
    };

    saveToDb = async (): Promise<DocumentSettingsSave> => {
        const saveData = this.getSaveDataFromState(this.state);
        //Call server
        const result = await AppUtils.callService(() => documentService.saveDocumentSettings(saveData.obj));
        if (!result) return null;
        this.orgStateHash = this.getSaveDataFromState(this.state).hash;
        return result;
    };

    handleSave = async (): Promise<boolean> => {
        const result = await this.saveToDb();
        if (!result) return false;
        return result.refreshFolders;
    };

    handleCancel = async (): Promise<IDocumentOtherSettingsCancelResult> => {
        const result: IDocumentOtherSettingsCancelResult = {
            canChangePage: true,
            refreshFolders: false
        };
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(this.state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) {
            result.canChangePage = false;
            return result;
        }
        if (cancelResult === ConfirmationDialogResult.Yes) {
            const saveResult = await this.saveToDb();
            result.canChangePage = !!saveResult;
            result.refreshFolders = saveResult?.refreshFolders ?? false;
            return result;
        }
        return result;
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.initialized) return null;
        return (
            <DialogSettingsCard
                fitToContainerHeight
                title={props.sessionState.isSuperAdmin ? Translations.OtherSettings : Translations.VersionHistory}
                content={
                    <div style={{ padding: "16px" }}>
                        {props.sessionState.isSuperAdmin &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.IgnoreFolderPatterns} margin={0}>
                                        <TextField name="ignoreFolderPatterns" title={Translations.IgnoreFolderPatterns} inputProps={{ maxLength: 50 }} value={state.ignoreFolderPatterns} required={true} onChange={this.handleChange} size="small" fullWidth />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.IgnoreFilePatterns} margin={0}>
                                        <TextField name="ignoreFilePatterns" title={Translations.IgnoreFilePatterns} inputProps={{ maxLength: 50 }} value={state.ignoreFilePatterns} required={true} onChange={this.handleChange} size="small" fullWidth />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.AutoSaveVersionHistorySize} margin={props.sessionState.isSuperAdmin ? null : 0} >
                                    <IdTitleDropdown
                                        selectedId={state.documentVersionAutoSaveHistorySize}
                                        items={[{ id: "0", title: Translations.NotInUse }].concat([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => { return { id: i.toString(10), title: i.toString(10) }; }))}
                                        onChange={(value) => this.setState({ documentVersionAutoSaveHistorySize: value })}
                                    />
                                    <Alert severity="info" sx={{ marginTop: ".5rem" }}>{Translations.DocumentModuleInfoAutoSaveVersionHistorySize}</Alert>
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.PublishVersionHistorySize} margin={props.sessionState.isSuperAdmin ? null : 0} >
                                    <IdTitleDropdown
                                        selectedId={state.documentVersionPublishHistorySize}
                                        items={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => { return { id: i.toString(10), title: i.toString(10) }; })}
                                        onChange={(value) => this.setState({ documentVersionPublishHistorySize: value })}
                                    />
                                    <Alert severity="info" sx={{ marginTop: ".5rem" }}>{Translations.DocumentModuleInfoPublishVersionHistorySize}</Alert>
                                </FormField>
                            </Grid>
                        </GridRow>
                        <Alert severity="info" sx={{ marginTop: "1rem" }}>{Translations.DecreasingVersionHistorySizeRemovesCurrentlySavedVersionHistoryRecords}</Alert>
                    </div>
                }
            />
        );
    }
};
